import LiveBotIndicator from "./LiveBotIndicator";
import { useState, useEffect } from "react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { BrandLogo } from "./BrandLogo";
import {
  LayoutDashboard, CandlestickChart, Bot, History, Wallet, Shield,
  TrendingUp, Menu, X, LogOut, Bell, ChevronDown, Gift, LifeBuoy, ShieldCheck,
  ArrowDownToLine, ArrowUpFromLine, Settings, Grid3x3, Monitor, Check, Sun, Moon, Newspaper,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useMode } from "../context/ModeContext";
import { useSettings } from "../context/SettingsContext";
import { useTheme, paletteIsLight } from "../context/ThemeContext";
import { toast } from "sonner";
import { usd, timeAgo } from "../lib/format";
import api from "../lib/api";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/terminal", label: "Trading Terminal", icon: CandlestickChart },
  { to: "/bots", label: "Trading Bots", icon: Bot },
  { to: "/history", label: "Trading History", icon: History },
  { to: "/wallet", label: "Wallet", icon: Wallet },
  { to: "/news", label: "News", icon: Newspaper },
  { to: "/referrals", label: "Referrals", icon: Gift },
  { to: "/kyc", label: "Verification", icon: ShieldCheck },
  { to: "/security", label: "Security", icon: Shield },
  { to: "/support", label: "Support", icon: LifeBuoy },
];

function NotificationsBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <button className="relative text-fx-s3 hover:text-white h-9 w-9 grid place-items-center rounded-md hover:bg-white/5" data-testid="notifications-btn">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-neg text-white text-[9px] font-bold grid place-items-center">{data.unread > 9 ? "9+" : data.unread}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-fx-s2 border-white/10 text-white w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
          <span className="text-sm font-semibold">Notifications</span>
          {data.unread > 0 && <button onClick={markAll} className="text-xs text-brandblue" data-testid="bell-mark-read">Mark all read</button>}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && <div className="px-3 py-6 text-center text-fx-s3 text-sm">No notifications.</div>}
          {data.items.slice(0, 8).map((n) => (
            <div key={n.id} className={`px-3 py-2 border-b border-white/[0.03] ${!n.read ? "bg-brandblue/[0.06]" : ""}`}>
              <div className="text-sm font-medium">{n.title}</div>
              <div className="text-xs text-fx-s3">{n.message}</div>
              <div className="text-[10px] text-fx-s3 mt-0.5">{timeAgo(n.created_at)}</div>
            </div>
          ))}
        </div>
        <button onClick={() => navigate("/notifications")} className="w-full text-center text-xs text-brandblue py-2 hover:bg-white/5">View all</button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ModeSwitch() {
  const { mode, setMode, balance } = useMode();
  const isDemo = mode === "demo";
  return (
    <div className="flex items-center gap-3" data-testid="mode-switcher">
      <div className={`hidden sm:flex flex-col items-end leading-tight`}>
        <span className={`text-[10px] font-mono uppercase tracking-widest ${isDemo ? "text-brandblue" : "text-gold"}`}>
          {isDemo ? "Demo Account" : "Live Account"}
        </span>
        <span className="font-mono font-semibold text-white text-sm">{usd(balance)}</span>
      </div>
      <div className="relative flex items-center rounded-full border border-white/10 bg-fx-s1 p-1">
        <button
          data-testid="switch-demo-btn"
          onClick={() => setMode("demo")}
          className={`relative z-10 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${isDemo ? "bg-brandblue text-white" : "text-fx-s3 hover:text-white"}`}
        >DEMO</button>
        <button
          data-testid="switch-live-btn"
          onClick={() => setMode("live")}
          className={`relative z-10 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${!isDemo ? "bg-gold text-black" : "text-fx-s3 hover:text-white"}`}
        >LIVE</button>
      </div>
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, logout, stopImpersonation, setUser } = useAuth();
  const { mode } = useMode();
  const { general, themes, theme } = useSettings();
  const { commitPalette } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const isAdmin = ["admin", "super_admin", "assistant"].includes(user?.role);
  const impersonating = user?._impersonator;

  const exitImpersonation = async () => {
    try { await stopImpersonation(); navigate("/admin"); } catch { logout(); navigate("/login"); }
  };

  const pickTheme = async (id, silent = false) => {
    try {
      await api.put("/me/theme", { id });
      if (id) {
        const p = (themes || []).find((t) => t.id === id)?.palette;
        if (p) commitPalette(p);
      }
      setUser({ ...user, theme_preference: id });
      if (!silent) toast.success(id ? "Theme updated" : "Using platform default theme");
    } catch { toast.error("Could not update theme"); }
  };

  // One-click light/dark toggle: flips the user's theme preference between the
  // light theme and Midnight (dark). Predictable two-target switch.
  const effId = user?.theme_preference || theme?.id;
  const effPalette = (themes || []).find((t) => t.id === effId)?.palette || theme?.palette;
  const isLight = paletteIsLight(effPalette);
  const toggleMode = () => pickTheme(isLight ? "midnight" : "professional-light", true);

  // Admins get a deliberately minimal sidebar (Dashboard + Admin Panel); every
  // module remains available inside the Admin Panel tabs. Customers see full nav.
  const items = isAdmin
    ? [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }, { to: "/admin", label: "Admin Panel", icon: Shield }]
    : nav;

  const bottomItems = [
    { to: "/dashboard", label: "Home", icon: LayoutDashboard },
    { to: "/wallet", label: "Wallet", icon: Wallet },
    { to: "/wallet?action=deposit", label: "Deposit", icon: ArrowDownToLine, match: "__deposit" },
    { to: "/wallet?action=withdraw", label: "Withdraw", icon: ArrowUpFromLine, match: "__withdraw" },
    { to: "/security", label: "Settings", icon: Settings },
  ];
  const isBottomActive = (it) => it.match ? false : location.pathname === it.to;

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-5 border-b border-white/5">
        <BrandLogo logoUrl={general.logo_url} siteName={general.site_name || "Fx Cloud"} textClass="text-white" />
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            onClick={() => setOpen(false)}
            data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-brandblue/15 text-white border border-brandblue/30" : "text-fx-s3 hover:text-white hover:bg-white/5 border border-transparent"
              }`
            }
          >
            <it.icon className="h-4 w-4" /> {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/5">
        <div className={`rounded-lg p-3 text-xs ${mode === "demo" ? "bg-brandblue/10 border border-brandblue/20" : "bg-gold/10 border border-gold/20"}`}>
          <div className={`font-mono uppercase tracking-widest ${mode === "demo" ? "text-brandblue" : "text-gold"}`}>{mode} mode active</div>
          <p className="text-fx-s3 mt-1 leading-relaxed">{mode === "demo" ? "Trading with virtual funds. Nothing at risk." : "Trades run on internal engine. Fund your live wallet to trade."}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="app-shell min-h-screen bg-fx-base text-white flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/5 bg-fx-s1/50 fixed inset-y-0">
        <SidebarInner />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-fx-s1 border-r border-white/10"><SidebarInner /></div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
          <button className="absolute top-4 right-4 text-white" onClick={() => setOpen(false)}><X className="h-6 w-6" /></button>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-4 lg:px-6 backdrop-blur-xl bg-fx-base/80 border-b border-white/5">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-fx-s3" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></button>
          </div>
          <div className="flex items-center gap-3">
            <LiveBotIndicator />
            <ModeSwitch />
            <button
              onClick={toggleMode}
              data-testid="theme-mode-toggle"
              title={isLight ? "Switch to dark" : "Switch to light"}
              className="text-fx-s3 hover:text-white h-9 w-9 grid place-items-center rounded-md hover:bg-white/5 transition-colors"
            >
              {isLight ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </button>
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border border-white/10 bg-fx-s1 pl-1 pr-2 py-1 hover:bg-fx-s2 transition-colors" data-testid="user-menu-btn">
                  <div className="h-7 w-7 rounded-full bg-brandblue grid place-items-center text-xs font-bold">
                    {(user?.name || "U").charAt(0).toUpperCase()}
                  </div>
                  <ChevronDown className="h-4 w-4 text-fx-s3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-fx-s2 border-white/10 text-white w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-fx-s3 font-mono">{user?.email}</div>
                  <div className="text-[10px] uppercase tracking-widest text-brandblue mt-1">{user?.role?.replace("_", " ")}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <div className="px-2 py-1.5" data-testid="user-theme-picker">
                  <div className="text-[10px] uppercase tracking-widest text-fx-s3 mb-2">Appearance</div>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => pickTheme(null)}
                      title="Platform default"
                      data-testid="theme-pick-default"
                      className={`h-7 w-7 rounded-full grid place-items-center border transition-all ${!user?.theme_preference ? "border-brandblue ring-2 ring-brandblue/40" : "border-white/15 hover:border-white/40"}`}
                    >
                      <Monitor className="h-3.5 w-3.5 text-fx-s3" />
                    </button>
                    {(themes || []).map((t) => {
                      const sel = user?.theme_preference === t.id;
                      return (
                        <button
                          key={t.id}
                          onClick={() => pickTheme(t.id)}
                          title={t.name}
                          data-testid={`theme-pick-${t.id}`}
                          className={`h-7 w-7 rounded-full grid place-items-center border transition-all overflow-hidden ${sel ? "border-brandblue ring-2 ring-brandblue/40" : "border-white/15 hover:border-white/40"}`}
                          style={{ background: `linear-gradient(135deg, ${t.palette.s1} 0 50%, ${t.palette.brandblue} 50% 100%)` }}
                        >
                          {sel && <Check className="h-3.5 w-3.5" style={{ color: t.palette.fg }} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => navigate("/wallet")} className="cursor-pointer focus:bg-white/10">Wallet</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn" className="cursor-pointer text-neg focus:bg-white/10 focus:text-neg">
                  <LogOut className="h-4 w-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 min-w-0 pb-20 lg:pb-0">
          {impersonating && (
            <div data-testid="impersonation-banner" className="bg-gold text-black text-sm font-semibold px-4 lg:px-6 py-2 flex items-center justify-between gap-3">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Viewing <b>{user?.email}</b> as admin <b>{impersonating.email}</b> — actions are audited.</span>
              <Button onClick={exitImpersonation} data-testid="exit-impersonation-btn" size="sm" className="h-7 bg-black text-white hover:bg-black/80">Exit customer account</Button>
            </div>
          )}
          {general.maintenance_mode && !impersonating && (
            <div data-testid="maintenance-banner" className="bg-gold/15 border-b border-gold/30 text-gold text-xs font-medium px-4 lg:px-6 py-2 flex items-center gap-2">
              <Shield className="h-3.5 w-3.5" /> Maintenance mode is ON — only admins can access the platform. Customers see a maintenance page.
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-fx-s1/95 backdrop-blur-xl border-t border-white/10 flex items-stretch" data-testid="mobile-bottom-nav" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {bottomItems.map((it) => (
          <button
            key={it.label}
            onClick={() => navigate(it.to)}
            data-testid={`bottomnav-${it.label.toLowerCase()}`}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${isBottomActive(it) ? "text-brandblue" : "text-fx-s3 hover:text-white"}`}
          >
            <it.icon className="h-5 w-5" /> {it.label}
          </button>
        ))}
        <button onClick={() => setMore(true)} data-testid="bottomnav-more" className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium text-fx-s3 hover:text-white">
          <Grid3x3 className="h-5 w-5" /> More
        </button>
      </nav>

      {/* More sheet */}
      {more && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end" data-testid="mobile-more-sheet">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMore(false)} />
          <div className="relative bg-fx-s1 border-t border-white/10 rounded-t-2xl p-4 pb-8 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <span className="font-display font-bold">Menu</span>
              <button onClick={() => setMore(false)} className="text-fx-s3"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {items.map((it) => (
                <button
                  key={it.to}
                  onClick={() => { setMore(false); navigate(it.to); }}
                  data-testid={`more-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex flex-col items-center gap-2 rounded-xl border p-3 text-xs font-medium transition-colors ${location.pathname === it.to ? "bg-brandblue/15 border-brandblue/30 text-white" : "bg-fx-base border-white/5 text-fx-s3"}`}
                >
                  <it.icon className="h-5 w-5" /> {it.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
